import { previewAmbient, setAmbientVolume } from '../audio/ambient';
import * as fx from '../audio/fx';
import * as engine from '../app/engine';
import { app, baseSettings, commit, eraseAll, on, replaceState } from '../app/state';
import { CLAMPS } from '../core/constants';
import { dailyToCSV, sessionsToCSV } from '../core/csv';
import {
  assertBackupSize,
  BackupError,
  backupFileName,
  emptyData,
  makeBackup,
  parseBackup,
} from '../core/storage';
import { applySettings } from '../core/timer';
import type { AmbientKind, Settings, SoundKind } from '../core/types';
import { clamp, dayKey, fmtDuration } from '../core/util';
import { notificationPermission, requestNotifications } from '../platform/notify';
import { wakeLockSupported } from '../platform/wakelock';
import { $, $$, animate, armConfirm, download, holdable, ic } from './dom';
import { toast } from './toast';

const paints: (() => void)[] = [];
const s = () => app.data.settings;

type NumKey = 'focus' | 'short' | 'long' | 'interval' | 'dailyGoal';
type BoolKey = 'autoBreak' | 'autoFocus' | 'ticking' | 'wakeLock';

function stepper(key: NumKey, label: string, unit: string, fmt: (v: number) => string = String) {
  const w = document.createElement('div');
  w.className = 'stepper';
  w.dataset.step = key; // names the − / + clicks in usage stats
  w.innerHTML = `<span class="s-lab"></span>
    <div class="s-cluster" role="group">
      <button class="s-btn" type="button" data-d="-1">${ic('minus')}</button>
      <b class="s-val" aria-live="polite"></b>
      <button class="s-btn" type="button" data-d="1">${ic('plus')}</button>
    </div>
    <span class="s-unit" aria-hidden="true"></span>`;
  w.querySelector('.s-lab')!.textContent = label;
  w.querySelector('.s-unit')!.textContent = unit;
  w.querySelector('.s-cluster')!.setAttribute('aria-label', label);
  const [minus, plus] = w.querySelectorAll<HTMLButtonElement>('.s-btn');
  minus!.setAttribute('aria-label', `Decrease ${label.toLowerCase()}`);
  plus!.setAttribute('aria-label', `Increase ${label.toLowerCase()}`);
  const val = w.querySelector<HTMLElement>('.s-val')!;
  const [lo, hi] = CLAMPS[key];
  const paint = () => {
    val.textContent = fmt(s()[key]);
    minus!.disabled = s()[key] <= lo;
    plus!.disabled = s()[key] >= hi;
  };
  for (const b of [minus!, plus!])
    holdable(b, () => {
      const d = Number(b.dataset.d);
      const v = clamp(s()[key] + d, lo, hi);
      if (v === s()[key]) return;
      engine.setSetting(key, v);
      fx.tick();
      animate(
        val,
        [
          { transform: `translateY(${d > 0 ? 60 : -60}%)`, opacity: 0 },
          { transform: 'none', opacity: 1 },
        ],
        {
          duration: 150,
          easing: 'cubic-bezier(.2,.7,.3,1)',
        },
      );
    });
  paints.push(paint);
  return w;
}

function toggle(key: BoolKey, label: string) {
  const w = document.createElement('label');
  w.className = 'switch';
  w.dataset.sw = key; // names the switch in usage stats
  w.innerHTML = `<input type="checkbox" role="switch"><span class="sw" aria-hidden="true"><i></i></span><span class="sw-lab"></span>`;
  w.querySelector('.sw-lab')!.textContent = label;
  const inp = w.querySelector('input')!;
  inp.addEventListener('change', () => {
    engine.setSetting(key, inp.checked);
    fx.tap();
  });
  paints.push(() => (inp.checked = s()[key]));
  return w;
}

function chips<K extends 'sound' | 'ambient'>(
  sel: string,
  attr: string,
  key: K,
  onPick: (v: Settings[K]) => void,
) {
  const btns = $$<HTMLButtonElement>(`${sel} button`);
  for (const b of btns)
    b.addEventListener('click', () => {
      const v = b.dataset[attr] as Settings[K];
      engine.setSetting(key, v);
      onPick(v);
    });
  paints.push(() => {
    for (const b of btns) {
      const on = b.dataset[attr] === s()[key];
      b.classList.toggle('on', on);
      b.setAttribute('aria-pressed', String(on));
    }
  });
}

function range(
  id: string,
  valId: string,
  key: 'volume' | 'ambientVolume',
  apply: (v: number) => void,
  done?: () => void,
) {
  const r = $<HTMLInputElement>(id);
  r.addEventListener('input', () => {
    const v = Number(r.value);
    $(valId).textContent = String(v);
    apply(v);
    app.data.settings[key] = v;
  });
  r.addEventListener('change', () => {
    commit(['settings']);
    done?.();
  });
  paints.push(() => {
    r.value = String(s()[key]);
    $(valId).textContent = String(s()[key]);
    apply(s()[key]);
  });
}

function renderDurFoot() {
  const { focus: f, short: sh, long: l, interval: n } = s();
  $('#dfCycle').textContent = fmtDuration(f * n + sh * (n - 1) + l);
  $('#dfFocus').textContent = `${f * n} MIN`;
  $('#dfRest').textContent = `${sh * (n - 1) + l} MIN`;
}

function paintNotif() {
  const btn = $<HTMLButtonElement>('#notifBtn');
  const note = $('#notifNote');
  const p = notificationPermission();
  if (p === 'unsupported') {
    btn.disabled = true;
    note.textContent = 'Not supported by this browser.';
    return;
  }
  btn.textContent =
    p === 'granted' ? 'ALERTS ENABLED' : p === 'denied' ? 'ALERTS BLOCKED' : 'ENABLE BROWSER ALERTS';
  btn.disabled = p !== 'default';
  note.textContent =
    p === 'granted'
      ? 'A quiet system ping when a phase ends and this tab is hidden.'
      : p === 'denied'
        ? 'Unblock notifications for this site in your browser settings.'
        : 'Only used while this tab sits in the background.';
}

function bindData() {
  $('#exportJson').addEventListener('click', () => {
    download(backupFileName(Date.now()), makeBackup(app.data, Date.now()), 'application/json');
    toast('Backup downloaded');
  });
  // One download per click: browsers block or prompt on a second automatic download.
  $('#exportCsv').addEventListener('click', () => {
    download(`pomodoro-sessions-${dayKey(Date.now())}.csv`, sessionsToCSV(app.data.history), 'text/csv');
    toast('Session log CSV downloaded');
  });
  $('#exportDailyCsv').addEventListener('click', () => {
    download(`pomodoro-daily-${dayKey(Date.now())}.csv`, dailyToCSV(app.data.history), 'text/csv');
    toast('Daily totals CSV downloaded');
  });
  const file = $<HTMLInputElement>('#importFile');
  $('#importJson').addEventListener('click', () => file.click());
  file.addEventListener('change', async () => {
    const f = file.files?.[0];
    file.value = '';
    if (!f) return;
    try {
      assertBackupSize(f.size); // before reading it into memory
      const data = parseBackup(await f.text(), Date.now());
      const prev = app.data;
      replaceState(data);
      engine.syncRunEffects();
      toast('Backup imported', {
        color: 'var(--sage)',
        action: { label: 'UNDO', run: () => (replaceState(prev), engine.syncRunEffects()) },
      });
    } catch (e) {
      toast(e instanceof BackupError ? e.message : 'Could not read that file.', 'var(--tomato)');
    }
  });

  $('#restoreDefaults').addEventListener('click', () => {
    const prev = s();
    app.data.settings = { ...baseSettings(), theme: prev.theme };
    // A session in progress keeps running at its own length; an idle timer picks up the defaults.
    app.data.run = applySettings(app.data.run, app.data.settings);
    commit(['settings', 'run']);
    engine.syncRunEffects();
    toast('Settings restored to defaults', {
      action: {
        label: 'UNDO',
        run: () => {
          app.data.settings = { ...prev, theme: s().theme };
          app.data.run = applySettings(app.data.run, app.data.settings);
          commit(['settings', 'run']);
          engine.syncRunEffects();
        },
      },
    });
  });
  armConfirm($<HTMLButtonElement>('#clearToday'), () => {
    const k = dayKey(Date.now());
    const d = app.data.history[k];
    if (d) {
      app.data.totals.sessions = Math.max(0, app.data.totals.sessions - d.sessions);
      app.data.totals.minutes = Math.max(0, app.data.totals.minutes - d.minutes);
      delete app.data.history[k];
    }
    commit(['stats']);
    toast("Today's log cleared");
  });
  armConfirm($<HTMLButtonElement>('#wipe'), () => {
    eraseAll();
    replaceState(emptyData({ theme: s().theme }));
    engine.syncRunEffects();
    toast('Everything erased');
  });
}

export function initSettings() {
  const dur = $('#durWrap');
  dur.append(
    stepper('focus', 'FOCUS', 'MIN'),
    stepper('short', 'SHORT BREAK', 'MIN'),
    stepper('long', 'LONG BREAK', 'MIN'),
    stepper('interval', 'LONG BREAK EVERY', 'ROUNDS'),
  );
  $('#goalWrap').append(stepper('dailyGoal', 'DAILY GOAL', 'SESSIONS', (v) => (v ? String(v) : 'OFF')));
  const flow = $('#flowWrap');
  flow.append(toggle('autoBreak', 'Auto-start breaks'), toggle('autoFocus', 'Auto-start focus'));
  if (wakeLockSupported()) flow.append(toggle('wakeLock', 'Keep screen awake while running'));
  $('#tickWrap').append(toggle('ticking', 'Ticking during focus'));

  chips('#soundChips', 's', 'sound', (v) => fx.chime(v as SoundKind));
  chips('#ambientChips', 'a', 'ambient', (v) => {
    if (v === 'off') engine.syncRunEffects();
    else previewAmbient(v as AmbientKind, engine.syncRunEffects);
  });
  range('#volRange', '#volVal', 'volume', fx.setVolume, () => fx.chime(s().sound));
  range('#ambRange', '#ambVal', 'ambientVolume', setAmbientVolume);

  $('#notifBtn').addEventListener('click', () => void requestNotifications().then(paintNotif));
  bindData();
  $('#appVersion').textContent = `v${__APP_VERSION__}`;

  paints.push(renderDurFoot);
  const paintAll = () => paints.forEach((p) => p());
  on('settings', paintAll);
  paintAll();
  paintNotif();
}
